import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, 
  Button, Container, Chip, CircularProgress, Grid, TableContainer, Divider, Stack 
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import InventoryIcon from "@mui/icons-material/Inventory";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { alpha } from "@mui/material/styles";
import { getPurchaseInvoiceById } from "../services/purchaseService";
import { toast } from 'react-toastify';

function PurchaseOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPurchaseInvoice();
  }, [id]);

  const loadPurchaseInvoice = async () => {
    try {
      setLoading(true);
      const data = await getPurchaseInvoiceById(id);
      setInvoice(data.payload || data);
    } catch (error) {
      console.error('Error loading purchase invoice:', error);
      toast.error('Failed to load purchase invoice');
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ pt: 8 }}>
        <Paper elevation={2} sx={{ p: 4, borderRadius: 3, textAlign: "center" }}>
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>Loading Purchase Invoice...</Typography>
        </Paper>
      </Container>
    );
  }

  if (!invoice) {
    return (
      <Container maxWidth="lg" sx={{ pt: 8 }}>
        <Paper elevation={3} sx={{ p: 6, borderRadius: 3, textAlign: "center", bgcolor: '#fff5f5' }}>
          <Typography variant="h5" color="error" mb={2} fontWeight="bold">
            ⚠️ Purchase Invoice Not Found
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            The requested purchase invoice could not be found in the system.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate("/purchases")}
            sx={{ borderRadius: 2, px: 4 }}
          >
            Back to Purchase Orders
          </Button>
        </Paper>
      </Container>
    );
  }

  const subTotal = invoice.SubTotal || 0;
  const totalTax = invoice.TaxAmount || 0;
  const totalAmount = invoice.TotalAmount || 0;
  const discountAmount = invoice.DiscountAmount || 0;

  // Format dates to DD/MM/YYYY
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pt: 2, pb: 4 }}>
      <Container maxWidth="xl">
        {/* Header with Back Button */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate("/purchases")}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Back to Purchases
          </Button>
          <Button 
            variant="contained" 
            startIcon={<EditIcon />} 
            onClick={() => navigate(`/purchases/edit/${id}`)}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#667eea',
              '&:hover': { bgcolor: '#5568d3' }
            }}
          >
            Edit Invoice
          </Button>
        </Box>

        <Grid container spacing={3}>
          {/* Supplier Information */}
          <Grid item xs={12} md={6}>
            {/* Supplier Details */}
            <Paper elevation={3} sx={{ p: 3, borderRadius: 3, mb: 3, border: '2px solid #e0e7ff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ bgcolor: '#e0e7ff', p: 1, borderRadius: 2, mr: 2 }}>
                  <PersonIcon sx={{ color: '#667eea', fontSize: 28 }} />
                </Box>
                <Typography variant="h6" fontWeight="bold" color="#667eea">
                  Supplier Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Supplier Name
                  </Typography>
                  <Typography variant="body1" fontWeight={600} color="text.primary">
                    {invoice.Supplier?.Name || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Contact Person
                  </Typography>
                  <Typography variant="body1" color="text.primary">
                    {invoice.Supplier?.Contactperson || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Phone Number
                  </Typography>
                  <Typography variant="body1" color="text.primary">
                    {invoice.Supplier?.PhoneNumber || invoice.Supplier?.MobileNumber || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Email
                  </Typography>
                  <Typography variant="body1" color="text.primary">
                    {invoice.Supplier?.Email || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    BTW Number
                  </Typography>
                  <Typography variant="body1" fontWeight={600} color="primary">
                    {invoice.Supplier?.BTWNumber || 'N/A'}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Invoice Information */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 3, border: '2px solid #e0e7ff', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ bgcolor: '#e0e7ff', p: 1, borderRadius: 2, mr: 2 }}>
                  <ReceiptIcon sx={{ color: '#667eea', fontSize: 28 }} />
                </Box>
                <Typography variant="h6" fontWeight="bold" color="#667eea">
                  Invoice Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Invoice Number
                  </Typography>
                  <Typography variant="body1" fontWeight={700} color="primary">
                    {invoice.PurchaseInvoiceNumber || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Reference Number
                  </Typography>
                  <Typography variant="body1" color="text.primary">
                    {invoice.ReferenceNumber || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Invoice Date
                  </Typography>
                  <Typography variant="body1" fontWeight={600} color="text.primary">
                    {formatDate(invoice.InvoiceDate)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Due Date
                  </Typography>
                  <Typography variant="body1" fontWeight={600} color="text.primary">
                    {formatDate(invoice.DueDate)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Payment Status
                  </Typography>
                  <Chip 
                    label={invoice.Status || 'Unknown'} 
                    sx={{ 
                      bgcolor: invoice.Status === 'Paid' ? '#10b981' : invoice.Status === 'Partial Paid' ? '#f59e0b' : '#ef4444',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      mt: 0.5
                    }} 
                  />
                </Box>
                {invoice.Status === 'Paid' && invoice.PaidDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                      Payment Date
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="success.main">
                      {formatDate(invoice.PaidDate)}
                    </Typography>
                  </Box>
                )}
                {invoice.Status === 'Paid' && invoice.PaymentMethod && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                      Payment Method
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="text.primary">
                      {invoice.PaymentMethod}
                    </Typography>
                  </Box>
                )}
                
                {/* Invoice Total */}
                <Divider sx={{ my: 2 }} />
                <Box sx={{ bgcolor: '#e0e7ff', p: 2, borderRadius: 2, mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Invoice Total
                  </Typography>
                  <Typography variant="h5" fontWeight={800} color="#667eea" sx={{ mt: 0.5 }}>
                    €{totalAmount.toFixed(2)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Line Items - Full Width */}
          <Grid item xs={12}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 3, border: '2px solid #dbeafe' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ bgcolor: '#dbeafe', p: 1, borderRadius: 2, mr: 2 }}>
                  <InventoryIcon sx={{ color: '#3b82f6', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="bold" color="#3b82f6">
                    Line Items
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {invoice.LineItems?.length || 0} item(s)
                  </Typography>
                </Box>
              </Box>
              
              <TableContainer sx={{ maxHeight: 500 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#f9fafb', fontWeight: 700, fontSize: '0.75rem' }}>#</TableCell>
                      <TableCell sx={{ bgcolor: '#f9fafb', fontWeight: 700, fontSize: '0.75rem' }}>Product</TableCell>
                      <TableCell sx={{ bgcolor: '#f9fafb', fontWeight: 700, fontSize: '0.75rem' }}>SKU</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#f9fafb', fontWeight: 700, fontSize: '0.75rem' }}>Qty</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#f9fafb', fontWeight: 700, fontSize: '0.75rem' }}>Unit Cost</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#f9fafb', fontWeight: 700, fontSize: '0.75rem' }}>Tax %</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#f9fafb', fontWeight: 700, fontSize: '0.75rem' }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoice.LineItems && invoice.LineItems.length > 0 ? (
                      invoice.LineItems.map((item, idx) => (
                        <TableRow key={item.Id || idx} hover sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                          <TableCell sx={{ fontSize: '0.85rem' }}>{idx + 1}</TableCell>
                          <TableCell sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{item.ProductName || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{item.ProductSKU || 'N/A'}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.85rem' }}>{item.Quantity || 0}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.85rem' }}>€{(item.UnitCost || 0).toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.85rem' }}>{item.TaxPercentage || 0}%</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#059669' }}>
                            €{(item.LineTotal || 0).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                          <Typography color="text.secondary" fontSize="0.9rem">No items found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Totals Section */}
              {invoice.LineItems && invoice.LineItems.length > 0 && (
                <Box sx={{ mt: 3, pt: 2, borderTop: '2px solid #e5e7eb' }}>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}>
                      <Typography variant="body1" fontWeight={600}>Subtotal:</Typography>
                      <Typography variant="body1" fontWeight={600}>€{subTotal.toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}>
                      <Typography variant="body1" fontWeight={600} color="text.secondary">Tax Amount:</Typography>
                      <Typography variant="body1" fontWeight={600} color="text.secondary">€{totalTax.toFixed(2)}</Typography>
                    </Box>
                    {discountAmount > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}>
                        <Typography variant="body1" fontWeight={600} color="error.main">Discount:</Typography>
                        <Typography variant="body1" fontWeight={600} color="error.main">-€{discountAmount.toFixed(2)}</Typography>
                      </Box>
                    )}
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, bgcolor: '#667eea', py: 2, borderRadius: 2, mt: 2 }}>
                      <Typography variant="h6" fontWeight={700} color="white">
                        Total Amount:
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="white">
                        €{totalAmount.toFixed(2)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Notes Section */}
        {invoice.Notes && (
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3, mt: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary" mb={2}>
              📝 Notes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {invoice.Notes}
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
}

export default PurchaseOrderDetailPage;